import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {Empty} from "data/types/generics";
import {ITournament} from "data/types/entities";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {TournamentStatus} from "data/enums";
import {DateTime} from "luxon";
import {LABEL_DATE_FORMAT} from "data/constants";
import {buildOrdinalNumber} from "data/utils/helpers";

interface IParams {
	tournamentId: number;
	small?: boolean;
}

enum PeriodEnum {
	PRE_MATCH = "prematch",
	HALF_TIME = "halftime",
	OVER_TIME = "overtime",
}

export interface IMatchStatusController extends ViewController<IParams> {
	get status(): TournamentStatus;

	get date(): string;

	get isLive(): boolean;

	period: (withDash: boolean) => string;

	update(params: IParams): void;
}

@injectable()
export class MatchStatusController implements IMatchStatusController {
	@observable protected _tournamentId: Empty<number>;
	@observable protected _smallLabels: boolean = false;

	constructor(@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore) {
		makeAutoObservable(this);
	}

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._tournamentId = param.tournamentId;
		this._smallLabels = !!param.small;
	}

	get status() {
		return this.tournament?.status || TournamentStatus.Scheduled;
	}

	get date() {
		if (!this.tournament) {
			return "-";
		}
		return DateTime.fromISO(this.tournament?.date).toFormat(LABEL_DATE_FORMAT);
	}

	get isLive(): boolean {
		if (this.tournament) {
			const {status} = this.tournament;
			return status === TournamentStatus.Playing;
		}

		return false;
	}

	protected get tournament(): Empty<ITournament> {
		return this._roundsStore.getTournamentById(this._tournamentId);
	}

	public period = (withDash: boolean) => {
		if (!this.tournament) {
			return "-";
		}
		const {activePeriod} = this.tournament;
		const label = this.phaseLabel(withDash);

		if (label) {
			return label;
		}

		const clockTime = this.getTime ? `${this.getTime} - ` : "";

		return `${clockTime}${buildOrdinalNumber(activePeriod)}`;
	};

	private get getTime() {
		const clock = this.tournament?.clock;

		if (!clock) {
			return null;
		}

		const splitClock = clock.split(":");
		const [hours, ...rest] = splitClock;

		if (splitClock.length === 2 || parseInt(hours)) {
			return clock;
		}

		return rest.join(":");
	}

	private phaseLabel(withDash: boolean) {
		const clockTime = this.getTime;

		const dash = withDash ? " - " : " ";
		const time = clockTime ? `${dash}${clockTime}` : "";

		const labels = !this._smallLabels
			? {
					[PeriodEnum.PRE_MATCH]: "PREMATCH",
					[PeriodEnum.HALF_TIME]: "HALFTIME",
					[PeriodEnum.OVER_TIME]: "OVERTIME",
			  }
			: {
					[PeriodEnum.PRE_MATCH]: "PRE",
					[PeriodEnum.HALF_TIME]: "HT",
					[PeriodEnum.OVER_TIME]: "OT",
			  };

		if (this.tournament?.isPreMatch) {
			return `${labels[PeriodEnum.PRE_MATCH]}${time}`;
		}
		if (this.tournament?.isHalfTime) {
			return `${labels[PeriodEnum.HALF_TIME]}${time}`;
		}
		if (this.tournament?.isOverTime) {
			return `${labels[PeriodEnum.OVER_TIME]}`;
		}
	}

	update(params: IParams) {
		this._tournamentId = params.tournamentId;
		this._smallLabels = !!params.small;
	}
}
